import React from "react";
import clsx from "classnames";

// material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import MuiButton from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";

// material-ui/icons
import ActionAdd from "@material-ui/icons/Add";
import ActionPrint from "@material-ui/icons/Print";
import HistoryIcon from "@material-ui/icons/History";
import EditIcon from "@material-ui/icons/Edit";
import FileIcon from "@material-ui/icons/InsertDriveFile";
import DeleteIcon from "@material-ui/icons/Delete";
import CircularProgress from "@material-ui/core/CircularProgress";

import {
  PageHeader,
  PageFooter,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  TextInput,
  SelectArrayInput,
  SelectInput,
  BackButton,
  DeleteButton,
  Button,
  FunctionField
} from "jazasoft";
import { MuiTable } from "@jazasoft/mui-table";

import CardHeader from "../../../components/CardHeader";
import FormDialog from "../../../components/FormDialog";
import HistoryDialog from "../../../components/HistoryDialog";
import ConfirmDialog from "../../../components/ConfirmDialog";

import { Role, EstimationState } from "../../../utils/types";
import hasPrivilege from "../../../utils/hasPrivilege";
import hasRole from "../../../utils/hasRole";
import { fieldOptions, inputOptions } from "../../../utils/helpers";

import useCostingView from "./useCostingView";
import SizeBreakupDrawer from "./SizeBreakupDrawer";

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    margin: theme.spacing(3),
    paddingBottom: theme.spacing(0.5)
  },
  card: {
    padding: 0,
    boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
    backgroundColor: "white",
    borderRadius: 8,
    overflow: "hidden"
  },
  sectionCard: {
    margin: "1.5em 0",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    border: "1px solid #e5e9f0",
    backgroundColor: "#fff"
  },
  padding: {
    padding: "1em 1.25em"
  },
  paddingBottom: {
    paddingBottom: "1.5em"
  },
  marginVertical: {
    margin: "1.5em 0"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.6rem 0.8rem",
    background: "#fafafa",
    borderTop: "1px solid #edf0f4"
  },
  techSheets: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5)
    }
  },
  footerAction: {
    color: "#4a5e71"
  },
  printAction: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(-1),
    marginBottom: theme.spacing(1)
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    paddingBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    gap: theme.spacing(1)
  },
  revisionWrapper: {
    borderRadius: 8,
    background: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
    border: "1px solid #e5e9f0",
    overflow: "hidden",
    marginTop: theme.spacing(2)
  },
  tabBar: {
    background: "#fbfcfe",
    borderBottom: "1px solid #e7ebf0"
  }
}));

const CostingView = props => {
  const classes = useStyles();
  const { roles, hasAccess } = props;

  const {
    revTabIdx,
    partIdx,
    costing,
    revisionCount,
    revisions,
    partList,
    columnsPart,
    rowsPart,
    columnsSizeBreakup,
    rowsSizeBreakup,
    columnsMarker,
    rowsMarker,
    columnsSummary,
    rowsSummaryMap,
    editing,
    reasonEditing,
    openFinalizeDialog,
    manuallyFinalizedRevNo,
    choicesTechSheets,
    choicesCostingReasons,
    choicesRevisions,
    busy,
    dialogState,
    onFooterActionClick,
    setRevTabIdx,
    setPartIdx,
    setEditing,
    setOpenFinalizeDialog,
    setDialogState,
    onFinalizeSubmit,
    onDetailsSubmit,
    onPartSubmit,
    onSizeBreakupSubmit,
    onMarkerSubmit,
    onDownloadTechSheet,
    onRevisionCreate,
    onCostingReasonsSubmit,
    onReasonEditingClose,
    onFinalizeDialogClose,
    onDeleteMiniMarker,
    onPrint,
    onDelete,
    isAttachmentUpload,
    attachColumnsMarker,
    drawerState,
    setDrawerState,
    onDrawerSubmit
  } = useCostingView(props);

  const isActiveRevision = revTabIdx + 1 === costing?.revisionCount;
  const isMerchant = hasRole(roles, [Role.MERCHANT]);
  const isAdmin = hasRole(roles, [Role.SUPER_USER, Role.ADMIN_USER]);
  const isCutPlanner = hasRole(roles, [Role.CUT_PLANNER]);
  const isEstCad = hasRole(roles, [Role.EST_CAD]);

  const isSizeBreakupEditable = rowsMarker.length === 0;

  const sizeBreakupFooterActions = [
    {
      name: "Update",
      tooltip: "Update",
      showLabel: true,
      icon: <EditIcon />,
      options: { variant: "outlined", color: "primary" }
    }
  ];

  const onUpdateSizeBreakup = (event, action, rows, filterValues, onActionComplete) => {
    setDrawerState({
      open: true,
      rows: rowsSizeBreakup,
      columns: columnsSizeBreakup,
      onActionComplete
    });
  };

  return (
    <div>
      <PageHeader title={`Costing #${props.id}`} />

      <SizeBreakupDrawer
        open={drawerState.open}
        rows={drawerState.rows}
        columns={drawerState.columns}
        onClose={() => {
          drawerState.onActionComplete && drawerState.onActionComplete(drawerState.rows);
          setDrawerState({ open: false, rows: [], columns: [] });
        }}
        onSubmit={onDrawerSubmit}
        onActionComplete={drawerState.onActionComplete}
      />

      <FormDialog title="Update Costing Details" open={editing} record={costing} onSubmit={onDetailsSubmit} onClose={() => setEditing(false)}>
        <TextInput source="fitValue" {...inputOptions(12)} />
        <TextInput source="styleDesc" {...inputOptions(12)} />
        <TextInput source="remarks" {...inputOptions(12)} />
        {(isAdmin || isMerchant) && <SelectArrayInput source="techSheetList" label="Tech Sheets" choices={choicesTechSheets} {...inputOptions(12)} />}
      </FormDialog>

      <FormDialog title="Costing Reasons" open={reasonEditing} onSubmit={onCostingReasonsSubmit} onClose={onReasonEditingClose}>
        <SelectInput source="reason" label="Costing Reasons" choices={choicesCostingReasons} {...inputOptions(12)} />
        <TextInput source="remarks" {...inputOptions(12)} />
      </FormDialog>

      <FormDialog
        title="Finalize Costing"
        record={{ revisionNo: manuallyFinalizedRevNo }}
        open={openFinalizeDialog}
        onSubmit={onFinalizeSubmit}
        onClose={onFinalizeDialogClose}>
        <SelectInput source="revisionNo" label="Costing Revisions" choices={choicesRevisions} {...inputOptions(12)} />
      </FormDialog>

      <HistoryDialog
        open={dialogState.action === "history" && dialogState.active}
        url={`costings/${props.id}/logs`}
        onClose={() => setDialogState({})}
      />

      <ConfirmDialog
        label="Delete"
        confirmTitle="Delete Mini Marker?"
        open={dialogState.action === "miniMarkerDelete" && dialogState.active}
        onCancel={() => setDialogState({})}
        onConfirm={onDeleteMiniMarker}
      />

      {Object.keys(costing).length > 0 && (
        <div className={classes.content}>
          <Card className={classes.sectionCard}>
            <CardHeader title="Style Details" />
            <Divider />
            <CardContent style={{ paddingBottom: 0 }}>
              <SimpleShowLayout record={costing} footer={false}>
                <TextField label="Costing Id" source="cstId" {...fieldOptions(3)} />
                <ReferenceField
                  source="cuChecklistId"
                  reference="cuChecklists"
                  linkType={record => `/estimation/cuChecklists/${record?.cuChecklistId}/view`}
                  {...fieldOptions(3)}>
                  <TextField source="checklistId" />
                </ReferenceField>
                <TextField label="Product" source="product.name" {...fieldOptions(3)} />
                <TextField label="Fit" source="fitValue" {...fieldOptions(3)} />
                <TextField label="Customer" source="customer.name" {...fieldOptions(3)} />
                <TextField label="Season" source="season.name" {...fieldOptions(3)} />
                <TextField source="colors" {...fieldOptions(6)} />
                <TextField source="styles" {...fieldOptions(3)} />
                <FunctionField
                  label="Tech Sheet"
                  render={record =>
                    record?.techSheets?.trim().length > 0 ? (
                      <div className={classes.techSheets}>
                        {record?.techSheetList?.map((filename, idx) => (
                          <Chip
                            key={idx}
                            label={filename}
                            variant="outlined"
                            component="a"
                            clickable
                            target="_blank"
                            rel="noopener noreferrer"
                            icon={<FileIcon />}
                            onClick={() => onDownloadTechSheet(filename)}
                          />
                        ))}
                      </div>
                    ) : (
                      "-"
                    )
                  }
                  {...fieldOptions(9)}
                />
                <TextField source="styleDesc" label="Style Description" {...fieldOptions(6)} />
                <TextField source="remarks" label="Remarks" {...fieldOptions(6)} />
              </SimpleShowLayout>
            </CardContent>
            <Divider />
            {costing?.state !== EstimationState.COMPLETED && (isAdmin || isMerchant || isCutPlanner) && (
              <div className={classes.footer}>
                <MuiButton color="primary" onClick={() => setEditing(true)}>
                  Edit
                </MuiButton>
              </div>
            )}
          </Card>

          {revisionCount > 1 && (
            <div
              className={clsx({
                [classes.card]: revisionCount > 1,
                [classes.paddingBottom]: partList.length > 1
              })}>
              <Card className={classes.sectionCard}>
                <CardHeader title="Costing Comparison" />
                <Divider />
                <div>
                  {partList.length > 1 && (
                    <Tabs className={classes.tabBar} value={partIdx} onChange={(_, idx) => setPartIdx(idx)} variant="scrollable" scrollButtons="auto">
                      {partList.map((part, idx) => (
                        <Tab key={idx} label={part} />
                      ))}
                    </Tabs>
                  )}
                  <MuiTable
                    columns={columnsSummary}
                    rows={rowsSummaryMap[partList[partIdx]]}
                    fontSize={15}
                    hasRowsChanged={rows => `${rows?.length}-${costing.modifiedAt}-${costing?.parts?.map(e => e.modifiedAt).join("-")}`}
                  />
                  <div className={classes.footer}>
                    <div>
                      <Button variant="outlined" color="primary" label="Finalize Costing" onClick={() => setOpenFinalizeDialog(true)}>
                        <EditIcon />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          <div
            className={clsx({
              [classes.revisionWrapper]: revisionCount > 1,
              [classes.paddingBottom]: partList.length > 1
            })}>
            {revisionCount > 1 && (
              <Tabs className={classes.tabBar} value={revTabIdx} onChange={(_, idx) => setRevTabIdx(idx)} variant="scrollable" scrollButtons="auto">
                {revisions.map((revision, idx) => (
                  <Tab key={idx} label={`Costing ${revision}`} />
                ))}
              </Tabs>
            )}

            {revisionCount > 1 && <Divider />}

            <div className={clsx({ [classes.padding]: revisionCount > 1 })}>
              <Card className={classes.sectionCard}>
                <CardHeader title="Parts" />
                <Divider />
                <div>
                  <MuiTable
                    columns={columnsPart}
                    rows={rowsPart}
                    editable={revTabIdx + 1 === costing?.revisionCount && (isAdmin || isMerchant || isEstCad)}
                    fontSize={15}
                    onSubmit={onPartSubmit}
                    hasRowsChanged={rows => `${rows.length}-${rows.map(e => e.id).join("-")}-${rows.map(e => e.modifiedAt).join("-")}`}
                  />
                </div>
              </Card>

              <Card className={classes.sectionCard}>
                <CardHeader title="Size Ratio" />
                <Divider />
                <div>
                  <MuiTable
                    columns={columnsSizeBreakup}
                    rows={rowsSizeBreakup}
                    editable={
                      revisionCount <= 1 &&
                      revTabIdx + 1 === costing?.revisionCount &&
                      costing?.state !== EstimationState.COMPLETED &&
                      (isAdmin || isMerchant)
                    }
                    footerActions={
                      revisionCount > 1 && costing?.state === EstimationState.NEW && isSizeBreakupEditable && (isAdmin || isMerchant)
                        ? sizeBreakupFooterActions
                        : undefined
                    }
                    onFooterActionClick={onUpdateSizeBreakup}
                    fontSize={15}
                    onSubmit={onSizeBreakupSubmit}
                    hasRowsChanged={rows => JSON.stringify(rows)}
                  />
                </div>
              </Card>

              {partList.map((part, idx) => {
                let footerActons = [];
                if (isActiveRevision && costing?.state !== EstimationState.COMPLETED && (isAdmin || isCutPlanner)) {
                  footerActons = [
                    { name: "upload", tooltip: "Upload", options: { classes: { root: classes.footerAction } } },
                    { name: "edit", tooltip: "Edit", options: { classes: { root: classes.footerAction } } }
                  ];
                  if (rowsMarker.filter(p => p.part === part)?.length === 0) {
                    footerActons = footerActons.filter(e => e.name !== "upload");
                  }
                }

                return (
                  <Card key={idx} className={classes.sectionCard}>
                    <CardHeader title={`Markers ${partList.length > 1 ? ` - ${part}` : ""}`} />
                    <Divider />
                    <div>
                      <MuiTable
                        key={isAttachmentUpload}
                        columns={isAttachmentUpload ? attachColumnsMarker : columnsMarker}
                        rows={rowsMarker.filter(p => p.part === part)}
                        editable={isActiveRevision && costing?.state !== EstimationState.COMPLETED && (isAdmin || isCutPlanner)}
                        showEditableActions={!isAttachmentUpload}
                        editing={isAttachmentUpload ? true : false}
                        footerActions={footerActons}
                        onFooterActionClick={onFooterActionClick(part)}
                        variant="excel"
                        emptyMessage="No Markers available!"
                        fontSize={15}
                        onSubmit={onMarkerSubmit(part)}
                        hasRowsChanged={rows => `${rows.length}-${rows.map(e => e.id).join("-")}-${rows.map(e => e.modifiedAt).join("-")}`}
                      />
                    </div>
                  </Card>
                );
              })}
            </div>

            {revisionCount > 1 && revTabIdx + 1 === revisionCount && (
              <div className={classes.printAction}>
                <Button
                  style={{ marginLeft: "1em", color: "red", borderColor: "red" }}
                  label={"Delete Costing Revision"}
                  variant="outlined"
                  disabled={busy}
                  onClick={onDelete}>
                  <DeleteIcon />
                  {busy && <CircularProgress size={20} thickness={2} />}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={classes.displayCenter}>
        <Button label="Print" variant="contained" color="primary" onClick={onPrint}>
          <ActionPrint />
        </Button>
      </div>

      {Object.keys(costing).length > 0 && (
        <PageFooter style={{ marginLeft: 24 }}>
          {hasPrivilege(roles, hasAccess, "costing", "update", "revise") && (
            <Button label="Costing" variant="contained" color="primary" disabled={busy} onClick={onRevisionCreate}>
              <ActionAdd />
            </Button>
          )}
          {hasPrivilege(roles, hasAccess, "est_log", "read") && (
            <Button
              label="History"
              variant="contained"
              style={{ marginLeft: "1em" }}
              onClick={() => setDialogState({ action: "history", active: true })}>
              <HistoryIcon />
            </Button>
          )}
          <DeleteButton
            style={{ marginLeft: "1em" }}
            variant="contained"
            resource="costings"
            basePath="/estimation/costings"
            record={costing}
            alertMessage="Are you sure you want to delete? Complete data will be deleted from here."
          />
          <BackButton variant="contained" color="default" style={{ marginLeft: "1em" }} />
        </PageFooter>
      )}
    </div>
  );
};

export default CostingView;
